import React from 'react';
import { Box, Typography, Container, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';

export default function CookiePolicy() {
    const cookiesData = [
        {
            name: '_ga',
            purpose: 'Google Analytics - Used to distinguish users',
            duration: '2 years',
            type: 'Analytics',
            provider: 'Google'
        },
        {
            name: '_gid',
            purpose: 'Google Analytics - Used to distinguish users',
            duration: '24 hours',
            type: 'Analytics',
            provider: 'Google'
        },
        {
            name: '_gat',
            purpose: 'Google Analytics - Used to throttle request rate',
            duration: '1 minute',
            type: 'Analytics',
            provider: 'Google'
        },
        {
            name: 'cookieConsentAccepted',
            purpose: 'Stores user cookie consent preferences',
            duration: '1 year',
            type: 'Necessary',
            provider: 'MeetMyBot'
        },
        {
            name: 'cookiePreferences',
            purpose: 'Stores detailed cookie category preferences',
            duration: '1 year',
            type: 'Necessary',
            provider: 'MeetMyBot'
        },
        {
            name: 'sessionId',
            purpose: 'Maintains user session for chat functionality',
            duration: 'Session',
            type: 'Necessary',
            provider: 'MeetMyBot'
        }
    ];

    return (
        <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: '#f8f9fa' }}>
            <Container maxWidth="md">
                <Box sx={{ bgcolor: 'white', borderRadius: 2, p: { xs: 3, md: 5 }, boxShadow: 1 }}>
                    <Typography variant="h3" fontWeight="bold" sx={{ mb: 3, color: '#1e293b' }}>
                        Cookie Policy
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 4, color: '#64748b' }}>
                        Last updated: {new Date().toLocaleDateString('en-EU')}
                    </Typography>

                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: '#1e293b' }}>
                        1. What Are Cookies
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                        Cookies are small text files that are placed on your device (computer, smartphone, or tablet) when you visit our website. They are widely used to make websites work more efficiently and provide information to website owners. Cookies contain information that is transferred to your device's hard drive and stored there.
                    </Typography>

                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: '#1e293b' }}>
                        2. How We Use Cookies
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                        We use cookies for several purposes:
                        <br />
                        • To ensure our website functions properly and securely
                        <br />
                        • To remember your preferences and settings
                        <br />
                        • To analyze how our website is used and improve user experience
                        <br />
                        • To provide personalized content and features
                        <br />
                        • To measure the effectiveness of our services
                    </Typography>

                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: '#1e293b' }}>
                        3. Types of Cookies We Use
                    </Typography>

                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, color: '#374151' }}>
                        Necessary Cookies
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                        These cookies are essential for the website to function properly. They enable basic functions like page navigation, access to secure areas, and remembering your cookie consent preferences. The website cannot function properly without these cookies.
                    </Typography>

                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, color: '#374151' }}>
                        Analytics Cookies
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                        These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This includes data about which pages are most visited, how long users spend on each page, and any error messages they encounter.
                    </Typography>

                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, color: '#374151' }}>
                        Marketing Cookies
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                        These cookies track your browsing habits to enable us to show advertising that is more likely to be of interest to you. They remember that you have visited our website and share this information with other organizations such as advertisers.
                    </Typography>

                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: '#1e293b' }}>
                        4. Cookie Duration
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                        <strong>Session Cookies:</strong> These are temporary cookies that expire when you close your browser. They are used to maintain your session while browsing our website.
                        <br />
                        <strong>Persistent Cookies:</strong> These remain on your device for a set period or until you delete them. They help us recognize you when you return to our website.
                    </Typography>

                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: '#1e293b' }}>
                        5. Third-Party Cookies
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                        Some cookies on our website are set by third-party services:
                        <br />
                        • <strong>Google Analytics:</strong> Helps us analyze website usage and improve user experience
                        <br />
                        • <strong>Eternity.ac:</strong> Powers our AI clone functionality and chat features
                        <br />
                        • <strong>Salesforce:</strong> Manages customer relationships and support interactions
                        <br />
                        These third parties have their own privacy policies governing their use of cookies.
                    </Typography>

                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: '#1e293b' }}>
                        6. Detailed Cookie Information
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                        Below is a detailed list of cookies used on our website:
                    </Typography>

                    <TableContainer component={Paper} sx={{ mb: 3 }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                                    <TableCell><strong>Cookie Name</strong></TableCell>
                                    <TableCell><strong>Purpose</strong></TableCell>
                                    <TableCell><strong>Duration</strong></TableCell>
                                    <TableCell><strong>Type</strong></TableCell>
                                    <TableCell><strong>Provider</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {cookiesData.map((cookie, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{cookie.name}</TableCell>
                                        <TableCell>{cookie.purpose}</TableCell>
                                        <TableCell>{cookie.duration}</TableCell>
                                        <TableCell>
                                            <Box
                                                sx={{
                                                    px: 1,
                                                    py: 0.5,
                                                    borderRadius: 1,
                                                    fontSize: '12px',
                                                    fontWeight: 'bold',
                                                    bgcolor: cookie.type === 'Necessary' ? '#dcfce7' :
                                                           cookie.type === 'Analytics' ? '#dbeafe' : '#fef3c7',
                                                    color: cookie.type === 'Necessary' ? '#166534' :
                                                           cookie.type === 'Analytics' ? '#1e40af' : '#92400e'
                                                }}
                                            >
                                                {cookie.type}
                                            </Box>
                                        </TableCell>
                                        <TableCell>{cookie.provider}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: '#1e293b' }}>
                        7. Managing Your Cookie Preferences
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                        You have several options to manage cookies:
                        <br />
                        <strong>Cookie Consent Banner:</strong> When you first visit our website, you can choose which categories of cookies to accept or reject.
                        <br />
                        <strong>Browser Settings:</strong> Most browsers allow you to control cookies through their settings. You can usually find cookie settings in the "Options" or "Preferences" menu.
                        <br />
                        <strong>Opt-out Tools:</strong> For advertising cookies, you can opt out through industry tools like the Network Advertising Initiative opt-out page.
                    </Typography>

                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: '#1e293b' }}>
                        8. Browser-Specific Cookie Management
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                        <strong>Google Chrome:</strong> Settings → Privacy and security → Cookies and other site data
                        <br />
                        <strong>Mozilla Firefox:</strong> Options → Privacy & Security → Cookies and Site Data
                        <br />
                        <strong>Safari:</strong> Preferences → Privacy → Manage Website Data
                        <br />
                        <strong>Microsoft Edge:</strong> Settings → Privacy, search, and services → Cookies and site permissions
                    </Typography>

                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: '#1e293b' }}>
                        9. Impact of Disabling Cookies
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                        If you disable cookies, some features of our website may not function properly:
                        <br />
                        • You may need to re-enter information each time you visit
                        <br />
                        • Personalized features and preferences may not work
                        <br />
                        • Some interactive features like our AI clone chat may be limited
                        <br />
                        • We won't be able to remember your cookie consent preferences
                    </Typography>

                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: '#1e293b' }}>
                        10. GDPR and ePrivacy Compliance
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                        In accordance with the General Data Protection Regulation (GDPR) and the ePrivacy Directive, we:
                        <br />
                        • Obtain explicit consent before setting non-essential cookies
                        <br />
                        • Provide clear information about all cookies we use
                        <br />
                        • Allow you to withdraw consent at any time
                        <br />
                        • Ensure consent is freely given, specific, informed, and unambiguous
                        <br />
                        • Respect your right to refuse non-essential cookies
                    </Typography>

                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: '#1e293b' }}>
                        11. Updates to This Policy
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                        We may update this Cookie Policy periodically to reflect changes in our practices or applicable laws. When we make changes, we will update the "Last updated" date at the top of this policy. We encourage you to review this policy regularly.
                    </Typography>

                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: '#1e293b' }}>
                        12. Contact Us
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                        If you have any questions about our use of cookies or this Cookie Policy, please contact us:
                        <br />
                        Email: contact@eternity.ac
                        <br />
                        Subject line: "Cookie Policy Inquiry"
                        <br />
                        <br />
                        For more information about your privacy rights and how we handle personal data, please see our Privacy Policy.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
}